import { call, put, select, takeEvery } from 'redux-saga/effects';
import {
    GET_CONSOLIDATION_DETAIL,
    SAVE_CONSOLIDATION_DETAIL,
    SUBMIT_CONSOLIDATION_DETAIL,
    SET_CONSOLIDATION,
    CHECK_CONSOLIDATION_UPDATE,
    DOWNLOAD_APPROVAL_LETTER,
    GET_DOWNLOAD_DOCUMENT,
    RESET_CONSOLIDATION,
} from 'store/key';
import { produce } from 'utility/general';
import { setGlobalLoading } from 'store/global/reducer';
import API from 'api/call';
import { safeSaga } from 'store';
import {
    SAVE_BEFORE_NAVIGATION,
    CONSOLIDATION_CONFIRM,
    SAVE_BEFORE_DOWNLOAD_APPROVAL_LETTER,
} from 'utility/frontendMessage';
import store from 'store';
import { AMENDMENT_FORM, INIT_APPLICATION } from 'utility/postApprovalLookUp';

const getInitialState = () => ({
    detail: {
        decision: null,
        comment: null,
        decisionDate: null,
        approvalExpiryDate: null,
        refNo: null,
        uploadTempId: null,
        attendMeetingUserId: null,
    },
    uploadTempName: null,
    tempFile: null,
    remoteDetail: null,
    refNoValid: null,
    forceErrorDisplay: null,
    commentValid: null,
    decisionValid: null,
    id: null,
    rows: [],
    formStatus: null,
    loading: false,
});

function* getConsolidationDetail(action) {
    const { formId, documentType } = action.payload;
    const { consolidationDetail } = yield select();

    consolidationDetail.loading = true;
    yield put({
        type: SET_CONSOLIDATION,
        payload: consolidationDetail,
    });
    try {
        const result = yield call(API.consolidation.getConsolidationDetail, { documentType, docId: formId });
        const res = result.data.result;

        /** set detail data */
        const newDetail = consolidationDetail.detail;
        for (let key in newDetail) {
            consolidationDetail.detail[key] = res[key] ?? null;
        }

        consolidationDetail.remoteDetail = res;

        /** set attendMeetingUserId */
        let attendMeetingList = [];
        res.panelMembers.forEach((item) => {
            if (item.attendMeeting) {
                attendMeetingList.push(item.userId);
            }
        });

        consolidationDetail.remoteDetail.attendMeetingUserId = null;
        if (attendMeetingList.length) {
            consolidationDetail.detail.attendMeetingUserId = attendMeetingList;
            consolidationDetail.remoteDetail.attendMeetingUserId = attendMeetingList;
        }
        /** set attendMeetingUserId end */

        consolidationDetail.rows = res.panelMembers ?? [];
        consolidationDetail.formStatus = res.formStatus;
        consolidationDetail.uploadTempName = res.uploadTempName;
        consolidationDetail.id = res.id;
    } catch (error) {
        console.log('consolidation failed:', error);
    }
    consolidationDetail.loading = false;
    yield put({
        type: SET_CONSOLIDATION,
        payload: consolidationDetail,
    });
}

function* saveConsolidationDetail(action) {
    const { formId, callback, documentType } = action.payload;
    const { consolidationDetail } = yield select();

    setGlobalLoading(true);
    try {
        const data = {
            documentId: formId,
            documentType: documentType,
            id: consolidationDetail.id,
            decision: consolidationDetail.detail.decision,
            comment: consolidationDetail.detail.comment,
            decisionDate: consolidationDetail.detail.decisionDate
                ? new Date(consolidationDetail.detail.decisionDate)
                : null,
            approvalExpiryDate: consolidationDetail.detail.approvalExpiryDate
                ? new Date(consolidationDetail.detail.approvalExpiryDate)
                : null,
            refNo: consolidationDetail.detail.refNo,
            uploadTempId: consolidationDetail.detail.uploadTempId,
            attendMeetingUserId: consolidationDetail.detail.attendMeetingUserId,
        };

        yield call(API.consolidation.saveConsolidationDetail, data);

        yield getConsolidationDetail({
            payload: {
                formId,
                documentType,
            },
        });
        callback?.();
    } catch (error) {
        console.log('consolidation failed:', error);
    }
    setGlobalLoading(false);
}

const checkValid = (consolidationDetail, isPostApproval, documentType) => {
    const { refNoValid, commentValid, decisionValid } = consolidationDetail;

    const { decisionDate, uploadTempId } = consolidationDetail.detail;

    let valid = true;

    if (!refNoValid && !isPostApproval) {
        valid = false;
    }

    if (uploadTempId === null && [INIT_APPLICATION, AMENDMENT_FORM].includes(documentType)) {
        valid = false;
    }

    if (!commentValid) {
        valid = false;
    }

    if (!decisionValid) {
        valid = false;
    }

    if (!decisionDate) {
        valid = false;
    }

    return valid;
};

function* submitConsolidationDetail(action) {
    const { callback, isPostApproval, formId, showDialog, documentType } = action.payload;
    const { consolidationDetail } = yield select();

    consolidationDetail.forceErrorDisplay = true;

    if (checkValid(consolidationDetail, isPostApproval, documentType)) {
        const isSave = yield showDialog({
            description: CONSOLIDATION_CONFIRM,
        });
        if (!isSave) return;

        setGlobalLoading(true);
        try {
            let data = {
                documentId: formId,
                documentType: documentType,
                id: consolidationDetail.id,
                decision: consolidationDetail.detail.decision,
                comment: consolidationDetail.detail.comment,
                decisionDate: consolidationDetail.detail.decisionDate
                    ? new Date(consolidationDetail.detail.decisionDate)
                    : null,
                uploadTempId: consolidationDetail.detail.uploadTempId,
                attendMeetingUserId: consolidationDetail.detail.attendMeetingUserId,
            };

            if (!isPostApproval) {
                data.refNo = consolidationDetail.detail.refNo;
                data.approvalExpiryDate = consolidationDetail.detail.approvalExpiryDate
                    ? new Date(consolidationDetail.detail.approvalExpiryDate)
                    : null;
            }

            yield call(API.consolidation.submitConsolidationDetail, data);
            callback?.();
        } catch (error) {
            console.log('consolidation failed:', error);
        }
        setGlobalLoading(false);
    } else {
        yield put({
            type: SET_CONSOLIDATION,
            payload: consolidationDetail,
        });
    }
}

const checkSaveData = (consolidationDetail) => {
    const remoteDetail = consolidationDetail.remoteDetail;
    if (!remoteDetail) return false;

    let hasUpdated = false;
    const newDetail = consolidationDetail.detail;
    for (let key in newDetail) {
        if (newDetail[key] !== remoteDetail[key]) {
            if (key === 'attendMeetingUserId') {
                // check attendMeetingUserId
                const ids = newDetail[key];
                if (ids?.length) {
                    if (remoteDetail.attendMeetingUserId === null) {
                        hasUpdated = true;
                        break;
                    } else {
                        const newRemoteIds = JSON.stringify(remoteDetail.attendMeetingUserId.sort());
                        const newIds = JSON.stringify(ids.sort());
                        if (newRemoteIds !== newIds) {
                            hasUpdated = true;
                            break;
                        }
                    }
                } else {
                    if (remoteDetail.attendMeetingUserId !== null) {
                        hasUpdated = true;
                        break;
                    }
                }
            } else {
                hasUpdated = true;
                break;
            }
        }
    }

    return hasUpdated;
};

function* checkConsolidationUpdate(action) {
    const { callback, showDialog, formId, documentType } = action.payload;
    const { consolidationDetail } = yield select();

    const hasUpdated = checkSaveData(consolidationDetail);
    if (hasUpdated) {
        const res = yield showDialog({
            description: SAVE_BEFORE_NAVIGATION,
        });
        if (res) {
            yield saveConsolidationDetail({
                payload: {
                    formId,
                    documentType,
                    callback: () => callback?.(true),
                },
            });
        }
    } else {
        callback?.(false);
    }
}

function* downloadApprovalLetter(action) {
    const { showDialog, formId, msg, documentType } = action.payload;
    const { consolidationDetail } = yield select();
    const { decision, refNo, decisionDate } = consolidationDetail.detail;

    if (!decision || !refNo) {
        showDialog({
            description: msg,
            disableCancelButton: true,
        });
        return;
    }

    /** save comsolidation before download when: consolidationDetail.id === null or has data changed */
    const hasUpdated = checkSaveData(consolidationDetail);

    const downloadFunc = () => {
        store.dispatch({
            type: GET_DOWNLOAD_DOCUMENT,
            payload: {
                data: {
                    decision,
                    decisionDate,
                    refNo,
                    docType: documentType,
                    docId: formId,
                },
                apiUrl: API.consolidation.generateApprovalLetter,
            },
        });
    };

    if (consolidationDetail.id === null || hasUpdated) {
        const res = yield showDialog({
            description: SAVE_BEFORE_DOWNLOAD_APPROVAL_LETTER,
        });
        if (res) {
            yield saveConsolidationDetail({
                payload: {
                    formId,
                    documentType,
                },
            });
            downloadFunc();
        }
    } else {
        downloadFunc();
    }
}

const reducer = (state = getInitialState(), action) => {
    switch (action.type) {
        case RESET_CONSOLIDATION:
            return getInitialState();
        case SET_CONSOLIDATION:
            const newState = produce(state, (draft) => {
                Object.entries(action.payload).forEach(([key, value]) => {
                    draft[key] = action.payload[key];
                });
            });
            return newState;
        default:
            return state;
    }
};

export function* watchConsolidationDetail() {
    yield takeEvery(GET_CONSOLIDATION_DETAIL, safeSaga(getConsolidationDetail));
    yield takeEvery(SAVE_CONSOLIDATION_DETAIL, safeSaga(saveConsolidationDetail));
    yield takeEvery(SUBMIT_CONSOLIDATION_DETAIL, safeSaga(submitConsolidationDetail));
    yield takeEvery(CHECK_CONSOLIDATION_UPDATE, safeSaga(checkConsolidationUpdate));
    yield takeEvery(DOWNLOAD_APPROVAL_LETTER, safeSaga(downloadApprovalLetter));
}

export default reducer;
